import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  signal,
  computed,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { FormsModule, ReactiveFormsModule, FormControl } from "@angular/forms";
import { TabsModule } from "primeng/tabs";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import { DialogModule } from "primeng/dialog";
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from "primeng/autocomplete";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { ClientDocumentService } from "../../../../core/services/client-document.service";
import {
  SharedService,
  getBase64,
} from "../../../../core/services/shared.service";
import { LoaderService } from "../../../../core/services/loader.service";
import { ClientGroupService } from "../../../../core/services/client-group.service";
import { GlobalFileUploadService } from "../../../../shared/services/global-file-upload.service";
import { ProjectWorkflowService } from "../../../../core/services/project-workflow.service";
import {
  ClientDocumentFileDto,
  RenameClientDocumentDto,
} from "../../../../core/models/project-workflow/clientDocumentFileDto";
import { FileUploadDto } from "../../../../core/models/shared/fileUploadDto";
import { ClientGroupDetailDto } from "../../../../core/models/client-group/client-group-detail.dto";
import { ClientUserDto } from "../../../../core/models/client-group/client-user.dto";
import { GrantProjectAccessRequest } from "../../../../core/models/client-group/grant-project-access-request.dto";
import { ProjectAccessListDto } from "../../../../core/models/client-group/project-access-list.dto";
import {
  GlobalFileDragDropDirective,
  DroppedFile,
} from "../../../../shared/directives/global-file-drag-drop.directive";
import {
  AddGroupModalComponent,
  AvailableGroup,
  GroupUser,
} from "./add-group-modal/add-group-modal.component";
import { CreateGroupModalComponent } from "../../client-group-detail/create-group-modal/create-group-modal.component";
import { ClientPortalEmailModalComponent } from "./client-portal-email-modal/client-portal-email-modal.component";
import { SpinnerComponent } from "../../../../shared/components/spinner/spinner.component";
import { PDFDocument } from "pdf-lib";
import { finalize, lastValueFrom, switchMap, tap } from "rxjs";
import { ProductionWorkflowLineDto } from "../../../../core/models/project-workflow/productionWorkflowLineDto";
import {
  EmailRecipient,
  EmailAttachment,
} from "./client-portal-email-modal/client-portal-email-modal.component";
import { OrderSummaryService } from "../../../../core/services/order-summary.service";
import { ProjectSummaryRequestDto } from "../../../../core/models/project-workflow/projectSummaryRequestDto";
import { WorkflowStatusEnum } from "../../../../shared/enums/workflowStatusEnum";

export interface ProjectAccessUser {
  /** Identifier of an existing Nadel Portal user; undefined until the access is saved for a new user. */
  userId?: number;
  email: string;
  displayName: string;
  /** Indicates the user does not yet exist as a Nadel Portal user and will be invited on save. */
  isNew: boolean;
  /** Indicates the user is the project's primary buyer. */
  isPrimaryBuyer?: boolean;
  /**
   * Indicates the user's project access has been approved. `false` means the user has access but
   * receives no project or invitation email until an administrator approves the request.
   * Undefined is treated as approved.
   */
  isApproved?: boolean;
  /** Pending approval entries are shown for visibility only and are not part of editable active access. */
  isPendingOnly?: boolean;
}

export interface ProjectAccessGroup {
  groupId: number;
  name: string;
  users: GroupUser[];
  expanded: boolean;
  groupIsEnable: boolean;
}

@UntilDestroy()
@Component({
  selector: "jni-client-portal",
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    TabsModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    DialogModule,
    AutoCompleteModule,
    GlobalFileDragDropDirective,
    AddGroupModalComponent,
    CreateGroupModalComponent,
    ClientPortalEmailModalComponent,
    SpinnerComponent,
  ],
  templateUrl: "./client-portal.component.html",
  styleUrls: [
    "./client-portal.component.scss",
    "../../../../shared/directives/global-file-drag-drop.styles.scss",
  ],
})
export class ClientPortalComponent implements OnInit {
  @Input() clientPortalLink = "";

  @Input()
  get workflowId(): number {
    return this._workflowId;
  }

  set workflowId(value: number) {
    const next = value ?? 0;
    if (next === this._workflowId) {
      return;
    }

    this._workflowId = next;
    this.accessReady.set(false);
    this.projectAccessGroups = [];
    this.projectAccessUsers = [];

    // When the parent resolves a new workflow ID asynchronously (e.g. after project creation from
    // an order), ngOnInit has already run with workflowId = 0 and skipped loading. React here so
    // project access and documents are loaded once the real ID arrives.
    if (next) {
      this.getClientDocFiles();
      this.loadProjectAccess();
    }
  }

  private _workflowId = 0;
  @Input() customerAccountD365 = "";
  @Input() isDisableLink = false;

  /**
   * Whether the project's client has Client Portal access enabled. When `false`, the Client Access
   * section shows a deactivation message and the Client Document / Project Link tabs are read-only.
   */
  @Input() isClientPortalEnabled = true;

  @Input() isClientPortalAccessLoading = true;

  /** Route to the Client Groups screen, linked from the deactivation message. */
  readonly clientGroupLink = "/project-workflow/client-group";

  /** Route to the access approvals queue, linked from the pending-approval clock icon. */
  readonly accessApprovalLink = "/project-workflow/approval";

  /** D365 sales order identifier of the currently open project, used to load order lines. */
  @Input() salesOrderIdD365 = "";

  /** Order lines of the current project, used to collect the latest proof PDF per line. */
  @Input() orderLines: ProductionWorkflowLineDto[] = [];

  /** Project (job) name shown in the invitation email subject and body. */
  @Input() projectName = "";

  /** Customer / company display name used in the email subject and template. */
  @Input() clientName = "";

  /** Buyer email selected in the Order section buyer dropdown, used for approval-domain checks. */
  @Input() buyerEmail: string | null = null;

  /** Buyer display name used when automatically granting primary-buyer access. */
  @Input() buyerName = "";

  /** D365 customer account that owns the client groups shown in the Add Group popup. */
  @Input()
  get customerAccount(): string {
    return this._customerAccount;
  }

  set customerAccount(value: string) {
    const next = value ?? "";
    if (next === this._customerAccount) {
      return;
    }

    this._customerAccount = next;

    // The parent resolves this input asynchronously (after the workflow details load).
    // React when it finally arrives so the Add Group popup and additional-user
    // autocomplete populate instead of showing a false "No groups available" state.
    if (next) {
      this.loadClientUsersForAdditional();

      if (this.showAddGroupModal) {
        this.loadAvailableGroups();
      }
    }
  }

  private _customerAccount = "";
  @Output() linkCopied = new EventEmitter<void>();
  @Output() emailSent = new EventEmitter<void>();
  @Output() pdfDownloaded = new EventEmitter<void>();

  @ViewChild("clientFileInput") clientFileInput!: ElementRef<HTMLInputElement>;

  // Client Document properties
  clientDocFiles: ClientDocumentFileDto[] = [];
  fileObject: FileUploadDto[] = [];
  fileUrl = "";
  fileName = "";
  showSmallLoader = false;

  // Rename dialog
  showRenameFileDailog = false;
  renameFileId = 0;
  renameFileName = "";
  renameFileExtension = "";

  // Add/Edit link dialog
  showFileLinkModal = false;
  fileLinkModalHeader = "";
  fileLinkMode = "";
  fileLink = "";
  fileLinkName = "";
  fileLinkId = 0;

  // Nadel Portal tab properties
  showAddGroupModal = false;
  showCreateNewGroupModal = false;
  showAddAdditionalInput = false;
  showClientPortalEmailModal = false;

  /** Recipients (project buyer and additional users) passed to the email composition modal. */
  emailRecipients: EmailRecipient[] = [];

  /**
   * Consolidated proof PDF attachment passed to the email modal as the default,
   * non-removable attachment. Rebuilt each time the modal opens.
   */
  emailDefaultAttachment: EmailAttachment | null = null;

  /** Reactive control backing the "Add Additional" user autocomplete. */
  readonly additionalUserControl = new FormControl<string | ClientUserDto>("");

  /** Inline validation message shown beneath the "Add Additional" email input. */
  additionalEmailError = "";

  /** Existing Nadel Portal users for the client, used by the additional-user autocomplete. */
  clientUsers: ClientUserDto[] = [];

  /** Filtered suggestions shown in the additional-user autocomplete. */
  additionalSuggestions: ClientUserDto[] = [];

  /** Tracks whether project access has changed since it was last loaded or saved. */
  private _accessDirty = false;

  /** Identifiers of approval requests awaiting an administrator decision. */
  readonly pendingApprovalIds = signal<number[]>([]);

  /** View-only pending approval entries shown in the Project Access panel. */
  pendingProjectAccessUsers: ProjectAccessUser[] = [];

  /** Monotonically increasing counter identifying each access-loading request, used to discard a stale GET that resolves after a newer one is known to have started. */
  private _accessRequestSeq = 0;

  /** The `_accessRequestSeq` value as of the last local update to `pendingApprovalIds`; a GET started at or before this point is stale and must not overwrite it. */
  private _pendingApprovalsFreshSeq = 0;

  projectAccessUsers: ProjectAccessUser[] = [];

  projectAccessGroups: ProjectAccessGroup[] = [];
  readonly savingAccess = signal(false);
  readonly loadingAccess = signal(false);
  readonly validatingAdditional = signal(false);
  readonly resendingInvitation = signal(false);
  readonly accessReady = signal(false);
  readonly accessError = signal("");
  readonly accessBusy = computed(
    () =>
      this.savingAccess() ||
      this.loadingAccess() ||
      this.validatingAdditional() ||
      this.resendingInvitation()
  );
  selectedGroup: ProjectAccessGroup | null = null;

  /** Groups available for the client, shown as selectable rows in the Add Group popup. */
  availableGroups: AvailableGroup[] = [];

  /** Indicates the available groups are being loaded for the Add Group popup. */
  loadingGroups = false;

  constructor(
    private _toastr: JniToastrService,
    private _clientDocumentService: ClientDocumentService,
    private _projectWorkflowService: ProjectWorkflowService,
    private _sharedService: SharedService,
    private _loaderService: LoaderService,
    private _clientGroupService: ClientGroupService,
    private _globalFileUploadService: GlobalFileUploadService,
    private _orderSummaryService: OrderSummaryService,
    private _router: Router
  ) {}

  /** Opens the access approvals queue in a new tab from the pending-approval clock icon. */
  openAccessApprovals(): void {
    const url = this._router.serializeUrl(
      this._router.createUrlTree([this.accessApprovalLink])
    );
    window.open(url, "_blank");
  }

  ngOnInit(): void {
    // workflowId loading (getClientDocFiles + loadProjectAccess) is handled by the workflowId setter,
    // which fires both when the value is set synchronously before ngOnInit (existing projects) and
    // asynchronously after project creation from an order (new projects).

    // Additional-user data is loaded by the customerAccount setter once the value
    // is available; it may already be set synchronously by the time we reach here.
    if (this.customerAccount && this.clientUsers.length === 0) {
      this.loadClientUsersForAdditional();
    }
  }

  /** Loads the current project access (groups and individual users) for the workflow. */
  loadProjectAccess(): void {
    if (!this.workflowId || this.savingAccess()) return;
    const projectId = this.workflowId;
    const requestSeq = ++this._accessRequestSeq;
    this.loadingAccess.set(true);
    this.accessReady.set(false);
    this.accessError.set("");
    this._clientGroupService
      .getProjectAccess(projectId)
      .pipe(
        finalize(() => {
          if (projectId === this.workflowId) this.loadingAccess.set(false);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: access => {
          if (projectId !== this.workflowId) return;
          this.applyProjectAccess(access);
          this._applyPendingApprovalIds(access.pendingApprovalIds, requestSeq);
        },
        error: () => {
          if (projectId !== this.workflowId) return;
          this.projectAccessGroups = [];
          this.projectAccessUsers = [];
          this.pendingProjectAccessUsers = [];
          this.pendingProjectAccessUsers = [];
          this.accessError.set(
            "Unable to load current access. Refresh before editing or emailing recipients."
          );
        },
      });
  }

  /**
   * Determines whether an entry is awaiting approval. Unapproved entries have project access but
   * receive no project or invitation email until an administrator approves the request.
   * @param entry The individual user or group member view model.
   * @returns `true` when the entry is awaiting approval; otherwise, `false`.
   */
  isUnapproved(entry: { isApproved?: boolean }): boolean {
    return entry.isApproved === false;
  }

  /** Gets the domain portion of an email address, lower-cased and without the leading @. */
  private _emailDomain(email: string): string {
    const normalized = (email ?? "").trim().toLowerCase();
    const atIndex = normalized.lastIndexOf("@");
    if (atIndex <= 0 || atIndex === normalized.length - 1) {
      return "";
    }
    return normalized.slice(atIndex + 1);
  }

  /**
   * Records approval request identifiers reported by the Add Group popup so they are reflected
   * immediately, without waiting for the next project access refresh.
   * @param ids Identifiers of the approval requests just created.
   */
  onApprovalsSubmitted(ids: number[]): void {
    if (!ids.length) return;
    this._mergePendingApprovalIds(ids);
  }

  /** Merges newly known held-approval identifiers into the tracked list and marks it fresh. */
  private _mergePendingApprovalIds(ids: number[]): void {
    const merged = new Set([...this.pendingApprovalIds(), ...ids]);
    this.pendingApprovalIds.set([...merged]);
    this._pendingApprovalsFreshSeq = this._accessRequestSeq;
  }

  /**
   * Applies an authoritative `pendingApprovalIds` listing from a project access GET, unless a newer
   * local update (from `onApprovalsSubmitted` or a save result) is already known to supersede it.
   * @param ids The listing's pending approval identifiers.
   * @param requestSeq The `_accessRequestSeq` value captured when this GET was started.
   */
  private _applyPendingApprovalIds(ids: number[], requestSeq: number): void {
    if (requestSeq <= this._pendingApprovalsFreshSeq) return;
    this.pendingApprovalIds.set(ids ?? []);
  }

  /** Lower-cased emails of every individual user and group member awaiting approval. */
  private _unapprovedEmails(): Set<string> {
    const individuals = this.projectAccessUsers.filter(user =>
      this.isUnapproved(user)
    );
    const members = this.projectAccessGroups.flatMap(group =>
      group.users.filter(user => this.isUnapproved(user))
    );
    return new Set(
      [...individuals, ...members].map(user => user.email.toLowerCase())
    );
  }

  /** Maps the loaded project access listing into the Project Access view models. */
  private applyProjectAccess(access: ProjectAccessListDto): void {
    this.projectAccessGroups = (access?.groups ?? []).map(group => ({
      groupId: group.groupId,
      name: group.groupName,
      expanded: false,
      groupIsEnable: group.groupIsEnable,
      users: group.users.map(user => ({
        name: user.displayName,
        email: user.email,
        selected: true,
        userId: user.userId,
        isNew: false,
        isApproved: user.isApproved,
      })),
    }));

    this.projectAccessUsers = (access?.individualUsers ?? [])
      .map(user => ({
        userId: user.userId,
        email: user.email,
        displayName: user.displayName,
        isNew: false,
        isPrimaryBuyer: user.isPrimaryBuyer,
        isApproved: user.isApproved,
      }))
      .sort((a, b) => Number(b.isPrimaryBuyer) - Number(a.isPrimaryBuyer));

    this.pendingProjectAccessUsers = (access?.pendingIndividualUsers ?? [])
      .map(user => ({
        userId: user.userId,
        email: user.email,
        displayName: user.displayName,
        isNew: false,
        isPrimaryBuyer: user.isPrimaryBuyer,
        isApproved: false,
        isPendingOnly: true,
      }))
      .filter(
        user =>
          !this.projectAccessUsers.some(
            active => active.email.toLowerCase() === user.email.toLowerCase()
          )
      )
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    this._accessDirty = false;
    this.accessReady.set(true);
  }

  /** Loads the client's existing users to power the additional-user autocomplete. */
  private loadClientUsersForAdditional(): void {
    this._clientGroupService
      .getClientUsers(this.customerAccount)
      .pipe(untilDestroyed(this))
      .subscribe(users => (this.clientUsers = users));
  }

  // Nadel Portal Methods
  openAddGroupModal() {
    if (this.accessBusy() || !this.accessReady()) return;
    this.showAddGroupModal = true;
    this.loadAvailableGroups();
  }

  /** Loads the client's groups from the API and maps them into selectable Add Group rows. */
  private loadAvailableGroups() {
    if (!this.customerAccount) {
      // The customer account is still being resolved by the parent. Keep the spinner
      // visible; the customerAccount setter re-triggers this load once it arrives.
      this.loadingGroups = true;
      this.availableGroups = [];
      return;
    }

    this.loadingGroups = true;
    this._clientGroupService
      .getAllGroupsByClient(this.customerAccount)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: groups => {
          this.availableGroups = groups
            .filter(group => group.isEnabled)
            .map(group => this.mapToAvailableGroup(group));
          this.loadingGroups = false;
        },
        error: () => {
          this.availableGroups = [];
          this.loadingGroups = false;
          this._toastr.error(
            "Error",
            "Unable to load groups. Please try again."
          );
        },
      });
  }

  /** Maps a group detail returned by the API into the Add Group popup view model. */
  private mapToAvailableGroup(group: ClientGroupDetailDto): AvailableGroup {
    this.selectedGroup =
      this.projectAccessGroups.find(
        selectedGroup => selectedGroup.name == group.groupName
      ) ?? null;

    const users = group.users.map(user => ({
      name: user.displayName,
      email: user.email,
      selected:
        this.selectedGroup?.users == null
          ? false
          : this.selectedGroup.users.some(
              u =>
                u.email.toLowerCase() === user.email.toLowerCase() && u.selected
            ),
      userId: user.userId,
      isNew: false,
    }));

    return {
      groupId: group.groupId,
      isEnabled: group.isEnabled,
      name: group.groupName,
      description: group.description,
      userCount: group.userCount,
      // Reflect the members' state: the group checkbox is checked when at least
      // one member is selected, and unchecked only when all are unchecked.
      selected: users.some(user => user.selected),
      expanded: false,
      showAddPerson: false,
      newPersonEmail: "",
      users,
    };
  }

  closeAddGroupModal() {
    this.showAddGroupModal = false;
  }

  openCreateNewGroupModal() {
    this.showAddGroupModal = false;
    this.showCreateNewGroupModal = true;
  }

  closeCreateNewGroupModal() {
    this.showCreateNewGroupModal = false;
    this.showAddGroupModal = true;
  }

  /**
   * Handles a successful group creation from the Create Group dialog by refreshing
   * the available groups and returning the user to the Add Group popup.
   */
  onGroupCreated() {
    this.showCreateNewGroupModal = false;
    this.showAddGroupModal = true;
    this.loadAvailableGroups();
  }

  onGroupsAdded(selectedGroups: AvailableGroup[]) {
    if (this.accessBusy() || !this.accessReady()) return;
    if (selectedGroups.length === 0) {
      this._toastr.warning("Warning", "Please select at least one group");
      return;
    }

    const previousGroups = [...this.projectAccessGroups];
    const skipped: string[] = [];

    selectedGroups.forEach(group => {
      const eligibleUsers: GroupUser[] = [];

      // Resolve the existing entry for this group up-front so that members
      // already belonging to this same group are not mistakenly counted as
      // "skipped duplicates" when the modal pre-selects previously-added groups.
      const existingIndex = this.projectAccessGroups.findIndex(
        g => g.groupId === group.groupId
      );
      const priorGroup =
        existingIndex !== -1 ? this.projectAccessGroups[existingIndex] : null;

      group.users
        .filter(u => u.selected)
        .forEach(user => {
          // Skip members who already have project access (individually or via another group)
          // so the same user is never duplicated; eligible members continue to be processed.
          if (this._hasProjectAccess(user.email)) {
            // Do not count as skipped when the access originates from this same group
            // being re-submitted because of pre-selection in the Add Group modal.
            const alreadyInThisGroup =
              priorGroup?.users.some(
                eu => eu.email.toLowerCase() === user.email.toLowerCase()
              ) ?? false;
            if (!alreadyInThisGroup) {
              skipped.push(user.name || user.email);
            }
          } else {
            eligibleUsers.push(user);
          }
        });

      if (existingIndex !== -1) {
        // Group already exists — merge newly selected eligible members with the
        // existing group's users so that users who were skipped (because they
        // already have access elsewhere) are not inadvertently removed.
        const existingGroup = this.projectAccessGroups[existingIndex];
        const mergedUsers = [...existingGroup.users];

        eligibleUsers.forEach(u => {
          const found = mergedUsers.some(
            mu => mu.email.toLowerCase() === u.email.toLowerCase()
          );
          if (!found) {
            mergedUsers.push(u);
          }
        });

        this.projectAccessGroups[existingIndex] = {
          ...existingGroup,
          users: mergedUsers,
        };
      } else {
        this.projectAccessGroups.push({
          groupId: group.groupId,
          name: group.name,
          users: eligibleUsers,
          expanded: false,
          groupIsEnable: group.isEnabled,
        });
      }
      this._accessDirty = true;
    });

    if (skipped.length > 0) {
      this._toastr.info(
        "Skipped",
        `${skipped.length} user(s) already had access and were skipped.`
      );
    }
    this.closeAddGroupModal();

    // Auto-save so group notification emails are sent immediately without a manual Save click; on
    // failure the added groups are rolled back so they do not linger in the list (Scenario 3).
    this._persistProjectAccess("Groups added successfully", () => {
      this.projectAccessGroups = previousGroups;
    });
  }

  toggleProjectAccessGroup(group: ProjectAccessGroup) {
    group.expanded = !group.expanded;
  }

  /** Determines whether the supplied email is already visible in the active or pending access list. */
  hasProjectAccess(email: string | null | undefined): boolean {
    const target = (email ?? "").trim().toLowerCase();
    if (!target) {
      return false;
    }

    return this._hasProjectAccess(target) || this._hasPendingApproval(target);
  }

  /** Grants the current buyer access in the Nadel Portal using the existing backend flow. */
  grantPrimaryBuyerAccess(): void {
    if (!this.workflowId || !this.buyerEmail) {
      this._toastr.warning(
        "Unavailable",
        "Select a buyer before granting access."
      );
      return;
    }

    const payload = {
      buyerEmail: this.buyerEmail.trim(),
      buyerName: (this.buyerName || this.buyerEmail).trim(),
      clientId: this.customerAccount || "",
      projectName: this.projectName || undefined,
    };

    this._loaderService.setLoaderState(true);
    this._clientGroupService
      .grantPrimaryBuyerAccess(this.workflowId, payload)
      .pipe(
        finalize(() => this._loaderService.setLoaderState(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: result => {
          const summaryMessage =
            result?.message || "Buyer access granted successfully.";
          this._toastr.success("Success", summaryMessage);
          this.loadProjectAccess();
        },
        error: (error: unknown) => {
          const message =
            error instanceof Error
              ? error.message
              : `Unable to grant buyer access to ${this.buyerEmail}.`;
          this._toastr.error("Error", message);
        },
      });
  }

  removeProjectAccessGroup(index: number) {
    if (this.accessBusy() || !this.accessReady()) return;
    this.projectAccessGroups.splice(index, 1);
    this._accessDirty = true;
    this._toastr.success("Success", "Group removed successfully");
  }

  toggleAddAdditionalInput() {
    if (this.accessBusy() || !this.accessReady()) return;
    this.showAddAdditionalInput = !this.showAddAdditionalInput;
    this.additionalEmailError = "";
    if (!this.showAddAdditionalInput) {
      this.additionalUserControl.reset("");
    }
  }

  /** Searches for client users via the backend API for the additional-user autocomplete. */
  searchAdditionalUsers(event: AutoCompleteCompleteEvent): void {
    const query = (event.query ?? "").trim();
    if (query.length < 2) {
      this.additionalSuggestions = [];
      return;
    }

    this._clientGroupService
      .searchClientUsers(this.customerAccount, query)
      .pipe(untilDestroyed(this))
      .subscribe(users => {
        this.additionalSuggestions = users.filter(
          u =>
            !this.projectAccessUsers.some(
              existing => existing.email.toLowerCase() === u.email.toLowerCase()
            )
        );
      });
  }

  /** Adds the selected existing client user to the additional users list. */
  onAdditionalUserSelect(event: AutoCompleteSelectEvent): void {
    const user = event.value as ClientUserDto;
    this.additionalEmailError = "";
    this._addAdditionalEmail(user.email);
  }

  addAdditionalUser() {
    const value = this.additionalUserControl.value;
    const email = typeof value === "string" ? value : (value?.email ?? "");
    this._addAdditionalEmail(email);
  }

  /**
   * Determines whether a user with the given email already has project access, either as an
   * individual user or as a member of an already-added group, so duplicates can be skipped.
   */
  private _hasProjectAccess(email: string): boolean {
    const target = email.trim().toLowerCase();
    return (
      this.projectAccessUsers.some(u => u.email.toLowerCase() === target) ||
      this.projectAccessGroups.some(g =>
        g.users.some(u => u.email.toLowerCase() === target)
      )
    );
  }

  /** Determines whether the email is already visible as a pending approval entry. */
  private _hasPendingApproval(email: string): boolean {
    const target = email.trim().toLowerCase();
    return this.pendingProjectAccessUsers.some(
      user => user.email.toLowerCase() === target
    );
  }

  /**
   * Adds an email to the additional users list, flagging it as new when it does not
   * match an existing client user so it can be highlighted and invited on save.
   */
  private _addAdditionalEmail(rawEmail: string): void {
    if (this.accessBusy() || !this.accessReady()) return;
    const email = (rawEmail ?? "").trim();
    if (!email) {
      this.additionalEmailError = "Enter an email address.";
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.additionalEmailError = "Enter a valid email address.";
      return;
    }

    if (this._hasProjectAccess(email)) {
      this.additionalEmailError = "This user already has access.";
      return;
    }

    if (this._hasPendingApproval(email)) {
      this.additionalEmailError = "This user is already awaiting approval.";
      return;
    }

    this.additionalEmailError = "";

    // Enforce the single-client rule before adding: an email already associated with another client
    // cannot be granted project access here. The backend owns the association and is the source of truth.
    this.validatingAdditional.set(true);
    this._clientGroupService
      .validateUserEmail(this.customerAccount, email)
      .pipe(
        finalize(() => this.validatingAdditional.set(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => this._commitAdditionalEmail(email),
        error: (error: unknown) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : "This email is already associated with another client.";
          this.additionalEmailError = message;
          this._toastr.error("Error", message);
        },
      });
  }

  /** Commits a validated additional email to the project access list and resets the input. */
  private _commitAdditionalEmail(email: string): void {
    const existing =
      this.clientUsers.find(
        u => u.email.toLowerCase() === email.toLowerCase()
      ) ??
      this.additionalSuggestions.find(
        u => u.email.toLowerCase() === email.toLowerCase()
      );

    this.additionalUserControl.reset("");
    this.showAddAdditionalInput = false;

    // The backend is the source of truth for buyer-domain and client-domain approval rules.
    // Use its returned pending-approval count to decide whether this was granted directly.
    this._persistProjectAccess("User added successfully", null, {
      email,
      displayName: existing?.displayName ?? email,
    });
  }

  removeProjectAccessUser(index: number) {
    if (this.accessBusy() || !this.accessReady()) return;
    this.projectAccessUsers.splice(index, 1);
    this._accessDirty = true;
    this._toastr.success("Success", "User removed successfully");
  }

  resendEmail(user: ProjectAccessUser) {
    if (this.accessBusy() || !this.accessReady()) return;
    if (this.isUnapproved(user)) {
      this._toastr.warning(
        "Pending approval",
        `${user.email} is awaiting approval. No email can be sent until the access request is approved.`
      );
      return;
    }
    if (!this.workflowId || typeof user.userId !== "number") {
      this._toastr.warning(
        "Unavailable",
        "Save project access before resending the invitation."
      );
      return;
    }

    this.resendingInvitation.set(true);
    this._loaderService.setLoaderState(true);
    this._clientGroupService
      .resendProjectAccessInvitation(this.workflowId, user.userId)
      .pipe(
        finalize(() => {
          this.resendingInvitation.set(false);
          this._loaderService.setLoaderState(false);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => {
          this._toastr.success("Success", `Invitation resent to ${user.email}`);
        },
        error: (error: unknown) => {
          this._toastr.error(
            "Error",
            error instanceof Error
              ? error.message
              : `Failed to resend invitation to ${user.email}. Please try again.`
          );
        },
      });
  }

  /**
   * Synchronises the configured project access with the backend. Invoked by the parent page on
   * Save: newly added users are granted access and invited; users removed in the UI are de-associated.
   * Does nothing when access has not changed since it was last loaded or saved.
   */
  saveProjectAccess(): void {
    if (
      !this.workflowId ||
      this.accessBusy() ||
      !this.accessReady() ||
      this.showAddGroupModal ||
      this.showCreateNewGroupModal
    ) {
      return;
    }

    // No local edits to push, but the backend may have changed access (e.g. the primary buyer was
    // swapped or removed by the header save), so refetch to reflect the current state.
    if (!this._accessDirty) {
      this.loadProjectAccess();
      return;
    }

    this._persistProjectAccess(null, null);
  }

  /** Builds the grant-project-access payload from the current groups and additional users. */
  private _buildProjectAccessPayload(): GrantProjectAccessRequest {
    return {
      entries: this.projectAccessGroups
        .map(group => ({
          groupId: group.groupId,
          userIds: group.users
            .map(user => user.userId)
            .filter((id): id is number => typeof id === "number"),
        }))
        .filter(entry => entry.userIds.length > 0),
      individualUsers: this.projectAccessUsers.map(user => ({
        email: user.email,
        displayName: user.displayName,
      })),
      clientId: this.customerAccount || undefined,
      projectName: this.projectName || undefined,
      // Reserved for a future resubmit/cancel flow; always null so the backend leaves existing
      // pending approvals untouched rather than reading an empty array as "cancel them all".
      pendingApprovalIds: null,
    };
  }

  /**
   * Persists the current project access to the backend, granting access and triggering invitation
   * emails. On failure the optional rollback restores the pre-change state so the user/group is not
   * left in the list, and no email is sent (Scenario 3).
   * @param successMessage Message shown on success; when null the backend result message is used.
   * @param rollback Invoked on failure to revert the optimistic add.
   */
  private _persistProjectAccess(
    successMessage: string | null,
    rollback: (() => void) | null,
    additionalUser?: { email: string; displayName: string }
  ): void {
    if (
      !this.workflowId ||
      this.savingAccess() ||
      this.loadingAccess() ||
      !this.accessReady()
    ) {
      return;
    }

    const payload = this._buildProjectAccessPayload();
    if (additionalUser) payload.individualUsers.push(additionalUser);
    const projectId = this.workflowId;
    // Snapshot the entries already awaiting approval so only newly flagged ones are announced.
    const previouslyUnapproved = this._unapprovedEmails();
    // Captured once the grant succeeds and the authoritative access GET is started; see
    // `_applyPendingApprovalIds` for why the GET's request sequence must be known at apply time.
    let requestSeq = 0;
    this.savingAccess.set(true);
    this.accessError.set("");
    this.showClientPortalEmailModal = false;
    this.emailRecipients = [];
    let saved = false;

    this._clientGroupService
      .grantProjectAccess(projectId, payload)
      .pipe(
        tap(result => {
          if (projectId !== this.workflowId) return;
          saved = true;
          this._accessDirty = false;
          const wasHeldForApproval = (result.pendingApprovals ?? 0) > 0;
          if (result.pendingApprovalIds?.length) {
            this._mergePendingApprovalIds(result.pendingApprovalIds);
          }
          if (wasHeldForApproval) {
            this._toastr.info(
              "Submitted for approval",
              "User added to the approval list because their domain differs from the buyer's domain."
            );
          }
          if (successMessage && !wasHeldForApproval) {
            this._toastr.success("Success", successMessage);
          } else if (
            !wasHeldForApproval &&
            (result.usersAdded > 0 || result.usersRemoved > 0)
          ) {
            this._toastr.success("Success", result.message);
          }
          if (result.emailConflictMessage)
            this._toastr.warning(
              "Access conflict",
              result.emailConflictMessage
            );
          // Discard optimistic group selection before reloading, including if the GET fails.
          this.projectAccessUsers = [];
          this.projectAccessGroups = [];
          this.pendingProjectAccessUsers = [];
          this.accessReady.set(false);
        }),
        tap(() => {
          requestSeq = ++this._accessRequestSeq;
        }),
        switchMap(() => this._clientGroupService.getProjectAccess(projectId)),
        finalize(() => this.savingAccess.set(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: access => {
          if (projectId !== this.workflowId) return;
          this.applyProjectAccess(access);
          this._applyPendingApprovalIds(access.pendingApprovalIds, requestSeq);
          if (
            !successMessage &&
            typeof payload.pendingApprovalIds === "undefined"
          ) {
            this._notifyNewlyUnapproved(previouslyUnapproved);
          }
        },
        error: () => {
          if (projectId !== this.workflowId) return;
          if (!saved && rollback) {
            rollback();
          }
          if (saved) {
            this.accessError.set(
              "Access saved, but current access could not be reloaded. Refresh before editing or emailing recipients."
            );
          }
          this._toastr.error(
            "Error",
            saved
              ? this.accessError()
              : "Unable to update project access. Please try again."
          );
        },
      });
  }

  /**
   * Announces entries that became unapproved as a result of the last save. Unapproved entries have
   * project access but receive no email until an administrator approves the request.
   * @param previouslyUnapproved Lower-cased emails that were already awaiting approval before saving.
   */
  private _notifyNewlyUnapproved(previouslyUnapproved: Set<string>): void {
    const added = [...this._unapprovedEmails()].filter(
      email => !previouslyUnapproved.has(email)
    );
    if (!added.length) return;
    this._toastr.info(
      "Submitted for approval",
      "User added to the approval list because their domain differs from the buyer's domain."
    );
  }

  // Client Document Methods
  getClientDocFiles() {
    this._loaderService.setLoaderState(true);
    this._clientDocumentService
      .getClientDocuments(this.workflowId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.status === "Success" && res?.data) {
            this.clientDocFiles = res.data;
          } else {
            this.clientDocFiles = [];
          }
          this._loaderService.setLoaderState(false);
        },
        error: res => {
          this._toastr.error("Error", res.message);
          this._loaderService.setLoaderState(false);
        },
      });
  }

  uploadClientDocument() {
    this.clientFileInput.nativeElement.click();
  }

  async onClientFileSelected(event: Event) {
    this.showSmallLoader = true;

    const inputElement = event.target as HTMLInputElement;
    if (!inputElement?.files || inputElement.files.length === 0) {
      this.showSmallLoader = false;
      return;
    }

    const fileObj = inputElement.files[0];
    const fileSize = Math.round(fileObj.size / 1024);
    const maxFileSize = 30 * 1024; // 30 MB

    if (fileSize > maxFileSize) {
      this._toastr.error(
        "Error",
        "File size exceeds 30MB. Please upload a smaller file."
      );
      this.showSmallLoader = false;
      return;
    }

    const allowedTypes = [
      ".ai",
      ".esp",
      ".ppt",
      ".pdf",
      ".jpeg",
      ".jpg",
      ".eps",
      ".pptx",
      ".xlsx",
      ".xls",
      ".docx",
      ".doc",
      ".dst",
      ".png",
    ];

    const fileExtension = "." + fileObj.name.split(".").pop()?.toLowerCase();
    const fileName = fileObj.name.split(".");
    fileName.pop();

    if (allowedTypes.includes(fileExtension)) {
      await getBase64(fileObj).then(base64Url => {
        const base64UrlString = base64Url as string;
        this.createFileUploadDto(
          base64UrlString,
          fileName[0],
          fileExtension,
          fileSize,
          fileExtension === ".dst" ? "application/octet-stream" : fileObj.type
        );
        this.fileName = fileObj.name;
        this.uploadFile(this.fileObject);
      });
    } else {
      this._toastr.error(
        "Error",
        "Invalid file type. Please upload one of the following file types: AI, ESP, PPT, PDF, JPEG, EPS, PPTX, XLSX, XLS, DOCX, DOC, DST, PNG"
      );
      this.showSmallLoader = false;
    }

    // Reset input so same file can be uploaded again
    inputElement.value = "";
  }

  createFileUploadDto(
    base64Url: string,
    name: string,
    extension: string,
    size: number,
    filemimetype: string
  ) {
    const base64File = base64Url.split(",")[1];
    this.fileObject = [];
    this.fileObject.push({
      base64: base64File,
      fileName: name,
      fileExtension: extension,
      fileSize: size,
      fileMimeType: filemimetype,
    });
  }

  uploadFile(fileObj: FileUploadDto[]) {
    this._projectWorkflowService
      .uploadProjectFiles(fileObj)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.status === "Success") {
            this.fileUrl = res.data;
            const clientDoc: ClientDocumentFileDto = {
              fileId: 0,
              projectWorkflowId: this.workflowId,
              customerAccountD365: this.customerAccountD365,
              fileName: this.fileName,
              fileUrl: res.data,
              isLink: false,
              size: fileObj[0].fileSize,
              fileMimeType: fileObj[0].fileMimeType,
              extension: fileObj[0].fileExtension,
              module: "ProjectWorkflow",
            };
            this.saveClientDocFile(clientDoc);
          } else {
            this.showSmallLoader = false;
            this._toastr.error("Error", res.message || "File upload failed");
          }
        },
        error: () => {
          this.showSmallLoader = false;
          this._toastr.error(
            "Error",
            "Failed to upload file. Please try again."
          );
        },
      });
  }

  saveClientDocFile(fileObj: ClientDocumentFileDto) {
    this._clientDocumentService
      .saveClientDocument(fileObj)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          if (res.status === "Success" && res.data) {
            fileObj.fileId = res.data;
            this.clientDocFiles.push(fileObj);
            this._toastr.success("File uploaded successfully");
          } else {
            this._toastr.error("Error", res.message || "File already exists");
          }
        },
        error: () => {
          this.showSmallLoader = false;
          this._toastr.error("Error", "Failed to save file. Please try again.");
        },
      });
  }

  downloadClientDocFile(url: string, name: string) {
    this.showSmallLoader = true;
    this._sharedService
      .getFileAsBlob(url)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          const mimeType = res.type || "application/octet-stream";
          const blob = new Blob([res], { type: mimeType });
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = name;
          link.click();
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        },
        error: () => {
          this._toastr.error("Error", `Unable to fetch File ${name}`);
          this.showSmallLoader = false;
        },
      });
  }

  deleteClientDocFileFromBlob(fileId: number, fileUrl: string) {
    const fileName = this.extractFileName(fileUrl) ?? "";
    this.showSmallLoader = true;
    this._projectWorkflowService
      .deleteFileFromBlob(fileName)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res && res.status === "Success") {
            this.deleteClientDocFile(fileId);
          } else {
            this._toastr.error("Error", "Unable to delete file");
          }
          this.showSmallLoader = false;
        },
        error: () => {
          this._toastr.error("Error", "Unable to delete file");
          this.showSmallLoader = false;
        },
      });
  }

  deleteClientDocFile(fileId: number) {
    this.showSmallLoader = true;
    this._clientDocumentService
      .deleteClientDocument({ fileId: fileId, workflowId: this.workflowId })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res && res.status === "Success" && res.data) {
            this._toastr.success("Success", "File deleted successfully");
            this.getClientDocFiles();
          } else {
            this._toastr.error("Error", "Unable to delete file");
          }
          this.showSmallLoader = false;
        },
        error: () => {
          this.showSmallLoader = false;
          this._toastr.error("Error", "Unable to delete file");
        },
      });
  }

  // Rename dialog methods
  openRenameFileDailog(fileId: number, fileName: string) {
    this.showRenameFileDailog = true;
    this.renameFileId = fileId;
    const lastDotIndex = fileName.lastIndexOf(".");
    this.renameFileName = fileName.substring(0, lastDotIndex);
    this.renameFileExtension = fileName
      .substring(lastDotIndex + 1)
      .toLowerCase();
  }

  closeRenameFileDailog() {
    this.showRenameFileDailog = false;
    this.renameFileName = "";
  }

  saveRenameFile() {
    if (this.renameFileName === "") {
      this._toastr.error("Error", "File Name cannot be empty");
      return;
    }

    const req: RenameClientDocumentDto = {
      newName: this.renameFileName + "." + this.renameFileExtension,
      projectWorkflowId: this.workflowId,
      fileId: this.renameFileId,
    };

    this.showSmallLoader = true;
    this._clientDocumentService
      .renameClientDocument(req)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          if (res?.data) {
            this.showRenameFileDailog = false;
            this._toastr.success("Success", "File renamed successfully");
            const newName =
              this.renameFileName + "." + this.renameFileExtension;
            const file = this.clientDocFiles.find(
              f => f.fileId === this.renameFileId
            );
            if (file) {
              file.fileName = newName;
            }
          } else {
            this._toastr.error("Error", "Failed to rename file");
          }
        },
        error: () => {
          this._toastr.error("Error", "Failed to rename file");
          this.showSmallLoader = false;
        },
      });
  }

  // Add/Edit link dialog methods
  addClientDocumentLink() {
    this.fileLinkModalHeader = "Add File Link";
    this.showFileLinkModal = true;
    this.fileLinkMode = "Add";
    this.fileLink = "";
    this.fileLinkName = "";
    this.fileLinkId = 0;
  }

  openEditLinkDailog(file: ClientDocumentFileDto) {
    this.fileLinkModalHeader = "Edit File Link";
    this.showFileLinkModal = true;
    this.fileLinkMode = "Edit";
    this.fileLink = file.fileUrl;
    this.fileLinkName = file.fileName;
    this.fileLinkId = file.fileId;
  }

  closeFileLinkDailog() {
    this.showFileLinkModal = false;
    this.fileLink = "";
    this.fileLinkName = "";
  }

  addFileLink() {
    const urlRegex = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i;
    if (this.fileLink === "" || this.fileLinkName === "") {
      this._toastr.error("Error", "File Link or Name cannot be empty");
      return;
    }

    if (!urlRegex.test(this.fileLink.trim())) {
      this._toastr.error("Error", "File Link should only contain a valid URL");
      return;
    }

    const payload: ClientDocumentFileDto = {
      fileId: 0,
      projectWorkflowId: this.workflowId,
      customerAccountD365: this.customerAccountD365,
      fileName: this.fileLinkName.trim(),
      fileUrl: this.fileLink.trim(),
      isLink: true,
      size: 0,
      fileMimeType: "",
      extension: "",
      module: "ProjectWorkflow",
    };

    this._loaderService.setLoaderState(true);
    this._clientDocumentService
      .saveClientDocument(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.status === "Success" && res.data) {
            payload.fileId = res.data;
            this.clientDocFiles.push(payload);
            this._toastr.success("Link added successfully");
            this.closeFileLinkDailog();
          } else {
            this._toastr.error("Error", res.message || "Link already exists");
          }
        },
      });
  }

  editFileLink() {
    if (this.fileLink === "" || this.fileLinkName === "") {
      this._toastr.error("Error", "File Link or Name cannot be empty");
      return;
    }

    const urlRegex = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i;
    if (!urlRegex.test(this.fileLink.trim())) {
      this._toastr.error("Error", "File Link should only contain a valid URL");
      return;
    }

    const projectFile = this.clientDocFiles.find(
      x => x.fileId === this.fileLinkId
    );

    if (!projectFile) {
      this._toastr.error("Error", "No File Found for Update");
      return;
    }

    projectFile.fileName = this.fileLinkName;
    projectFile.fileUrl = this.fileLink;

    this._loaderService.setLoaderState(true);
    this._clientDocumentService
      .editClientDocumentLink(projectFile)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.status === "Success" && res.data) {
            this._toastr.success("Link updated successfully");
            this.closeFileLinkDailog();
          } else {
            this._toastr.error("Error", res.message || "Link update failed");
          }
        },
      });
  }

  // Drag and drop
  onClientFilesDropped(droppedFiles: DroppedFile[]): void {
    if (this.isDisableLink) {
      return;
    }

    this._globalFileUploadService.processDroppedFiles(droppedFiles, {
      uploadMethod: (files: DroppedFile[]) => this.handleDroppedFiles(files),
      acceptedTypes: [
        "pdf",
        "doc",
        "docx",
        "jpg",
        "jpeg",
        "png",
        "txt",
        "xlsx",
        "xls",
        "dst",
      ],
      maxFileSize: 30,
      maxFiles: 10,
      showSuccessMessage: true,
      showErrorMessage: true,
    });
  }

  private handleDroppedFiles(droppedFiles: DroppedFile[]): void {
    droppedFiles.forEach(droppedFile => {
      const mockEvent = {
        target: { files: [droppedFile.file] },
      } as unknown as Event;
      this.onClientFileSelected(mockEvent);
    });
  }

  onClientFilesRejected(rejectedFiles: { file: File; reason: string }[]): void {
    this._globalFileUploadService.handleRejectedFiles(rejectedFiles);
  }

  // Utility methods
  getFileIconClass(fileName: string) {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "ai":
        return "/assets/images/file-icons/AI-Icon.svg";
      case "esp":
      case "eps":
        return "/assets/images/file-icons/ESP-Icon.svg";
      case "ppt":
      case "pptx":
        return "/assets/images/file-icons/PPT-Icon.svg";
      case "pdf":
        return "/assets/images/file-icons/PDF-Icon.svg";
      case "jpeg":
      case "jpg":
        return "/assets/images/file-icons/JPG-Icon.svg";
      case "xlsx":
      case "xls":
        return "/assets/images/file-icons/Excel-icon.svg";
      case "docx":
      case "doc":
        return "/assets/images/file-icons/Word-icon.svg";
      case "png":
        return "/assets/images/file-icons/PNG-Icon.svg";
      default:
        return "/assets/images/file-icons/PDF-Icon.svg";
    }
  }

  private extractFileName(url: string) {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    const fileName = params.get("fileName");
    return fileName ? decodeURIComponent(fileName) : null;
  }

  // Project Link Methods
  copyClientPortalLink() {
    navigator.clipboard
      .writeText(this.clientPortalLink)
      .then(() => {
        this._toastr.success("Success", "Link copied to clipboard");
        this.linkCopied.emit();
      })
      .catch(() => {
        this._toastr.error("Error", "Failed to copy link");
      });
  }

  openEmailTemplateModal() {
    if (!this.workflowId || this.accessBusy() || !this.accessReady()) {
      return;
    }

    this._loaderService.setLoaderState(true);
    this.emailRecipients = this.buildEmailRecipients();

    this.generateProjectSummaryPdf()
      .then(pdfBytes => {
        if (pdfBytes) {
          const base64 = this.uint8ArrayToBase64(pdfBytes);
          this.emailDefaultAttachment = {
            fileName: "Project Summary.pdf",
            fileSize: `${Math.round(pdfBytes.length / 1024)}kb size`,
            fileSizeBytes: pdfBytes.length,
            base64,
            contentType: "application/pdf",
            isDefault: true,
          };
        } else {
          this.emailDefaultAttachment = null;
        }
        this._loaderService.setLoaderState(false);
        this.showClientPortalEmailModal = true;
      })
      .catch(() => {
        this.emailDefaultAttachment = null;
        this._loaderService.setLoaderState(false);
        this._toastr.error(
          "Error",
          "Unable to generate Project Summary PDF. Please try again."
        );
      });
  }

  closeClientPortalEmailModal() {
    this.showClientPortalEmailModal = false;
  }

  onEmailSent() {
    this.showClientPortalEmailModal = false;
    this.emailSent.emit();
  }

  /**
   * Builds the default email recipients from the current project access buyer and additional users.
   * Users awaiting approval are excluded: they hold access but must not receive any email yet.
   */
  private buildEmailRecipients(): EmailRecipient[] {
    return this.projectAccessUsers
      .filter(
        user => typeof user.userId === "number" && !this.isUnapproved(user)
      )
      .map(user => ({
        email: user.email,
        displayName: user.displayName,
        recipientType: user.isPrimaryBuyer ? "Buyer" : "Additional",
        addType: "TO",
      }));
  }

  /**
   * Downloads the "Project Summary" PDF: the server-generated project summary page
   * (Project Shipment Summary layout) followed by the latest proof for every order line.
   * Proof files may be PDF, PNG, or JPEG — all are handled correctly.
   */
  downloadClientPortalPdf() {
    if (!this.workflowId) {
      return;
    }

    this._loaderService.setLoaderState(true);
    this.generateProjectSummaryPdf()
      .then(pdfBytes => {
        this._loaderService.setLoaderState(false);
        if (!pdfBytes) {
          this._toastr.warning(
            "No Content",
            "No summary or proof documents are available to download."
          );
          return;
        }
        const base64 = this.uint8ArrayToBase64(pdfBytes);
        const blob = this.base64ToBlob(base64, "application/pdf");
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "Project Summary.pdf";
        link.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        this.pdfDownloaded.emit();
      })
      .catch(() => {
        this._loaderService.setLoaderState(false);
        this._toastr.error(
          "Error",
          "Unable to generate Project Summary PDF. Please try again."
        );
      });
  }

  /**
   * Builds the full "Project Summary" PDF:
   * 1. Calls the backend to generate the project header page (Proposal #, consultant, Bill To, Ship To)
   *    with the workflow line items table (Line-Item, Nick Name, Quantity, Status).
   * 2. Appends the latest proof for every order line (supports PDF, PNG, JPEG).
   * Returns `null` when the resulting document has no pages.
   */
  private async generateProjectSummaryPdf(): Promise<Uint8Array | null> {
    const mergedPdf = await PDFDocument.create();

    // Step 1 — fetch the server-generated project summary with correct workflow line items.
    if (this.salesOrderIdD365) {
      const lineItems = this.orderLines.map((line, index) => ({
        lineItem: index + 1,
        nickName: line.nickName ?? "",
        quantity: line.quantityD365 ?? 0,
        approvalStatus: this.getApprovalStatusLabel(line),
      }));

      const request: ProjectSummaryRequestDto = {
        salesOrderId: this.salesOrderIdD365,
        lineItems,
      };

      const summaryRes = await lastValueFrom(
        this._orderSummaryService.ProjectSummaryDocument(request)
      );

      if (summaryRes?.status?.toLowerCase() === "success" && summaryRes.data) {
        const summaryBlob = this.base64ToBlob(
          summaryRes.data,
          "application/pdf"
        );
        const summaryBuffer = await summaryBlob.arrayBuffer();
        const summaryPdf = await PDFDocument.load(summaryBuffer, {
          ignoreEncryption: true,
        });
        const pages = await mergedPdf.copyPages(
          summaryPdf,
          summaryPdf.getPageIndices()
        );
        pages.forEach(page => mergedPdf.addPage(page));
      }
    }

    // Step 2 — append each line’s latest proof (PDF / PNG / JPEG).
    const proofUrls = await this.collectLatestProofUrls();
    for (const url of proofUrls) {
      await this.appendProofToPdf(mergedPdf, url);
    }

    if (mergedPdf.getPageCount() === 0) {
      return null;
    }

    return mergedPdf.save();
  }

  /**
   * Returns the human-readable approval / proof status label for a workflow line,
   * matching the same logic used in workflow-line-items.component.
   */
  private getApprovalStatusLabel(line: ProductionWorkflowLineDto): string {
    if (
      line.proofStatus === 2 ||
      line.lineStatus === WorkflowStatusEnum.ProofApproved
    ) {
      return "Approved";
    }
    if (
      line.proofStatus === 3 ||
      line.lineStatus === WorkflowStatusEnum.RevisionRequested
    ) {
      return "Change Request";
    }
    if (
      line.hasProof ||
      (line.lineStatus !== null &&
        line.lineStatus >= WorkflowStatusEnum.ProofSent)
    ) {
      return "Pending Approval";
    }
    return "";
  }

  /**
   * Appends a single proof file (PDF, PNG, or JPEG) to the in-progress merged PDF.
   * The file type is detected from the HTTP content-type returned by the server;
   * the URL extension is used as a fallback.
   */
  private async appendProofToPdf(
    mergedPdf: PDFDocument,
    url: string
  ): Promise<void> {
    const blob = await lastValueFrom(this._sharedService.getFileAsBlob(url));
    if (!blob) {
      return;
    }

    const buffer = await blob.arrayBuffer();
    const mimeType = blob.type.toLowerCase();
    const ext = (url.split("?")[0].split(".").pop() ?? "").toLowerCase();

    if (mimeType.includes("png") || ext === "png") {
      const image = await mergedPdf.embedPng(buffer);
      const { width, height } = this.fitImageToPage(image.width, image.height);
      const page = mergedPdf.addPage([width, height]);
      page.drawImage(image, { x: 0, y: 0, width, height });
    } else if (
      mimeType.includes("jpeg") ||
      mimeType.includes("jpg") ||
      ext === "jpg" ||
      ext === "jpeg"
    ) {
      const image = await mergedPdf.embedJpg(buffer);
      const { width, height } = this.fitImageToPage(image.width, image.height);
      const page = mergedPdf.addPage([width, height]);
      page.drawImage(image, { x: 0, y: 0, width, height });
    } else {
      // Default: treat as PDF
      const sourcePdf = await PDFDocument.load(buffer, {
        ignoreEncryption: true,
      });
      const pages = await mergedPdf.copyPages(
        sourcePdf,
        sourcePdf.getPageIndices()
      );
      pages.forEach(page => mergedPdf.addPage(page));
    }
  }

  /**
   * Scales image dimensions to fit within a standard A4 page (595 × 842 pt)
   * while preserving the aspect ratio. Images smaller than A4 are not upscaled.
   */
  private fitImageToPage(
    imgWidth: number,
    imgHeight: number
  ): { width: number; height: number } {
    const maxWidth = 595;
    const maxHeight = 842;
    const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight, 1);
    return {
      width: Math.round(imgWidth * ratio),
      height: Math.round(imgHeight * ratio),
    };
  }

  /**
   * Resolves the latest proof file URL for every order line in the project, keeping only one
   * (the most recent) proof per line and de-duplicating identical URLs.
   */
  private async collectLatestProofUrls(): Promise<string[]> {
    const urls: string[] = [];

    for (const line of this.orderLines) {
      if (!line?.lineRecIdD365) {
        continue;
      }

      const history = await lastValueFrom(
        this._projectWorkflowService.getLineItemProofHistory(
          this.workflowId,
          line.lineRecIdD365
        )
      );

      const latestProof = (history?.data?.history ?? []).find(
        entry => !!entry.fileUrl
      );

      if (latestProof?.fileUrl && !urls.includes(latestProof.fileUrl)) {
        urls.push(latestProof.fileUrl);
      }
    }

    return urls;
  }

  /** Converts a byte array into a base64 string without a data-URI prefix. */
  private uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  }

  /** Converts a base64 string into a Blob of the given content type. */
  private base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array<number>(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
  }
}
